/*
 * Authors: Koen, mariushirjanu
 * Created: 07/10/2015
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import * as Noty from 'noty';
import { Injectable } from '@angular/core';
import { DialogClickFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '../../../shared/message/ga-message.service';
import { INamedValue, IProductImage } from '../../../../type_definitions/datatypes';
import { IImage, ReorderImagesComponent } from '../../../shared/reorder-images/reorder-images.component';
import { GlobalProductDataService, IGlobalItem, IProductImagesHolder, IProperty, IPropertyGroup } from '../global-product-data.service';

import { GlobalProductService } from '../global-product.service';
import { FieldBuilder, GaFormlyService, IExtraFieldConfigurationObject, OnChangeHandler } from '../../../shared/formly/ga-formly.service';
import { ɵreverseDeepMerge as reverseDeepMerge } from '@ngx-formly/core';
import { ButtonAddonComponent } from '../../../shared/formly/addons/button-addon.component';

export const GLOBAL_PRODUCT_SAVE_ACTION:string = 'saveGlobalProduct';

@Injectable()
export class GlobalProductEditService {
  private _multipleFreeProperties:boolean;

  constructor(
    private formlyService:GaFormlyService,
    private globalProductService:GlobalProductService,
    private globalProductDataService:GlobalProductDataService,
    private messageService:GaMessageService
  ) {
    /***/
  }

  public showReorderImagesDialog(productId:number):Promise<IProductImagesHolder> {
    const productImages:IProductImagesHolder = this.globalProductService.images();
    const images:IProductImage[] = productImages?.images;

    return new Promise((resolve, reject) => {
      if(images?.length <= 1){
        this.messageService.showWarningDialog('Reorder images', 'You need at least 2 images to reorder');
        reject();
        return;
      }

      let buttons:IDialogButton[] = [];
      let model:IImage[] = [];
      const onSave:DialogClickFn = () => {
        if(model?.length){
          buttons[0].busy = true;
          const saveImageOrderMessage:Noty = this.messageService.showStatusMessage('Saving the order of images…');
          this.globalProductDataService.saveImages(productId, {images: model})
            .then((result:IProductImagesHolder) => {
              resolve(result);
            })
            .catch(reject)
            .finally(() => {
              buttons[0].busy = false;
              this.messageService.closeMessage(saveImageOrderMessage);
              dialog.hide();
            });
        } else {
          // no changes -> just close dialog
          dialog.hide();
          reject();
        }
      };

      buttons = [ {
        label: 'Save Order',
        className: 'btn-success',
        onClick: onSave
      }, {
        label: 'Don\'t Save',
        className: 'btn-default',
        onClick: () => {
          dialog.hide();
          reject();
        }
      } ];

      const dialog:IDialog = this.messageService.showDialog({
        title: 'Reorder images',
        component: ReorderImagesComponent,
        buttons,
        inputs: {
          images
        },
        outputs: {
          onChange: (newModel:IImage) => {
            model = util.deepClone(newModel);
          }
        },
        size: DialogSize.LG
      });

    });
  }

  public showDeleteImageDialog(productId:number, image:IProductImage):Promise<IProductImagesHolder> {
    return new Promise((resolve, reject) => {
      let buttons:IDialogButton[] = [];
      const onDelete:DialogClickFn = () => {
        buttons[0].busy = true;
        const deleteImageMessage:Noty = this.messageService.showStatusMessage('Deleting image…');
        const productImages:IProductImagesHolder = this.globalProductService.images();
        this.globalProductDataService.deleteImage(productId, image?.id)
          .then(() => {
            const updatedProductImages:IProductImage[] = _.without(productImages.images, _.findWhere(productImages.images, {
              id: image?.id
            }));

            resolve({
              images: updatedProductImages,
              modelImages: productImages.modelImages
            });
          })
          .catch(reject)
          .finally(() => {
            this.messageService.closeMessage(deleteImageMessage);
            dialog.hide();
            buttons[0].busy = true;
          });
      };

      buttons = [ {
        label: 'Delete Image',
        className: 'btn-danger',
        onClick: onDelete
      }, {
        label: 'Don\'t Delete',
        className: 'btn-default',
        onClick: () => {
          dialog.hide();
          reject();
        }
      } ];

      const dialog:IDialog = this.messageService.showDialog({
        title: 'Delete image',
        message: `<p>Are you sure you want to delete this image from the global product?</p><br/><img src="${image.url}" alt="" />`,
        buttons,
        size: DialogSize.LG
      });
    });
  }

  public showInvalidMessage(errorMessages:string[]):void {
    const title:string = 'Invalid values!';
    const message:string = '<p><i class="fa fa-warning"></i> There are invalid values on the product. Please change them before saving.</p>';
    this.messageService.showInvalidFormMessage(title, message, errorMessages);
  }

  public multipleFreeProperties(multiple?:boolean):boolean {
    if (arguments.length) {
      this._multipleFreeProperties = multiple;
    }
    return this._multipleFreeProperties;
  }

  public buildPropertyField(key:string = 'value', label:string, control:string, property:IProperty, propModel:any, required?:boolean, labelClass?:string, valueClass?:string, extraOptions?:IExtraFieldConfigurationObject, newField?:boolean):IExtraFieldConfigurationObject {
    let field:IExtraFieldConfigurationObject | FieldBuilder;
    let fieldGroup:IExtraFieldConfigurationObject;

    if (control === 'REAL') {
      field = this.formlyService.anInput(key, label)
        .withInputType('number');
      // build() is executed below
    } else if (control === 'BOOLEAN') {
      field = this.formlyService.aSelect(key, label)
        .withLabelProp('description')
        .withValueProp('value')
        .withOptions([
          {
            description: 'yes',
            value: true
          },
          {
            description: 'no',
            value: false
          }
        ])
        .withLabelClass(labelClass)
        .withValueClass(valueClass);
      // build() is executed below
    } else if (control === 'LIST') {
      field = this.formlyService.aSelect(key, label)
        .withOptions(property.values)
        .withLabelProp('description')
        .withValueProp('value')
        .withLabelClass(labelClass)
        .withValueClass(valueClass);
      // build() is executed below
    } else if (control === 'TEXT') {
      field = this.formlyService.anInput(key, label)
        .withLabelClass(labelClass)
        .withValueClass(valueClass);
      // build() is executed below
    }

    // set required fields
    if (required) {
      field = (<FieldBuilder>field).isRequired();
    }

    // add custom wrapper when the field is not unit
    if (property.units && property.units.length !== 1) {
      field = (<FieldBuilder>field)
        .withoutWrapper()
        .withWrapperClass('col-xsm-9 col-sm-10 col-xsm-hug-right col-sm-max-space-bottom');
    }

    // call some methods which apply for all field types and execute build()
    field = (<FieldBuilder>field)
      .withLabelClass('col-sm-3 mb-0')
      .withExtraOptions({
        model: propModel
      })
      .build();

    // add units
    if (property.units) {
      if (property.units.length === 1) {
        field.templateOptions.addOn = property.units[ 0 ].description;
      } else {
        const unitField = this.formlyService.aSelect('unit', 'Unit')
          .withFormat((p) => {
            return p.description;
          })
          .withoutWrapper()
          .withWrapperClass('col-xsm-3 col-sm-2 col-xsm-hug-left')
          .withOptions(property.units)
          .withExtraOptions({
            model: propModel
          })
          .build();

        // create group
        fieldGroup = this.formlyService.aGroup(undefined, label)
          .withLabelClass(`${labelClass} mb-0`)
          .withValueClass(valueClass)
          .withFields([
            field,
            unitField
          ])
          .build();
      }
    }

    const result:IExtraFieldConfigurationObject = fieldGroup || field;

    if (extraOptions) {
      reverseDeepMerge(result, extraOptions);
    }

    if (newField) {
      $('.added').removeClass('added');
      result.className = 'col col-12 added';
    }

    return result;
  }

  public buildFreePropertyField(model:IGlobalItem, property:INamedValue, newField?:boolean, onChangeFn?:OnChangeHandler, onDeleteFn?:Function):IExtraFieldConfigurationObject {
    const field:IExtraFieldConfigurationObject = this.formlyService.anInput(`_freeProperties.${util.toCamelCase(property.name)}`, property.name)
      .isRequired()
      .withLabelClass('col-sm-3 mb-0')
      .withValueClass('col-sm-9')
      .withAddOnButton({
        component: ButtonAddonComponent,
        inputs: {
          label: 'Delete',
          icon: 'fa-trash-o'
        },
        outputs: {
          onClick: () => onDeleteFn(model, property.name)
        }
      })
      .withExtraOptions({
        templateOptions: {
          name: property.name
        }
      })
      .onChange(onChangeFn)
      .build();

    if (newField) {
      $('.added').removeClass('added');
      field.className = 'col col-12 added';
    }

    return field;
  }

  // this method creates a model key for each property so that can be used later to update properties[] at onChange event
  public createFixedPropertyKey(categoryName:string, propertyName:string):string {
    const properties:IPropertyGroup[] = this.globalProductService.properties();
    const propertyCategoryIdx:number = _.findIndex(properties, { name: categoryName });
    const property:IProperty = _.findWhere(properties[propertyCategoryIdx].values, { name: propertyName });
    return `${categoryName.split(' ').join('_')}:${property.definitionId}`;
  }
}